/**
 * Relationship fields for collection views: the forward aggregates (tags, sources,
 * contacts by kind, times, attachments), each a reference-typed field with
 * `isArray: true` and a value accessor over the related rows, and the
 * reverse-direction fields that match a record by being the TARGET of a link
 * elsewhere (`reverse_links.{k}`, a `backlink`; `backlinks.{schemaId}.{k}`). The
 * server's filter builder keys its clauses on the `key` prefix and `subtype`, so
 * those strings are the wire format.
 */

import { enrichField } from "../collections/records.js"
import type { Schema } from "../registries/schemas.js"
import { withAppearance } from "./appearance.js"
import { type AugerField,type EnrichableField } from "./fields.js"

// STATIC FIELD MAP ============================================================

type Source = { source_type?: string }

/** Aggregate relation fields — read a whole record relation, not `data[key]`.
 *  Pure declarations: `enrichField` resolves each `value` off the relation
 *  (`taggings`/`contacts`/`times`/`attachments`/`sources` via `resolveDirect`,
 *  `emails`/`phones`/`socials`/`locations` via their contacts filter).
 *  `taggings` and `times` sort by their types' orderings: tags by name, lowest first,
 *  and times by `occurrence`. Order is preserved in the output. */
const FIELDS: ReadonlyArray<EnrichableField> = [
  // Keyed `taggings` — the actual relation on the record (there is no `tags`
  // field server-side). Label stays "Tags".
  {
    key: "taggings",
    type: "tagging",
    label: "Tags",
    icon: "tag",
    width: 200,
    isArray: true,
    isEditable: true,
    isFilterable: true,
  },
  {
    key: "sources",
    type: "source",
    label: "Sources",
    icon: "plug",
    width: 150,
    isArray: true,
    isEditable: false,
    isFilterable: true,
    // Value is the full `record_sources` rows (so the cell can link each source
    // by `source_id`); grouping is by `source_type`. Each group is a resolved
    // `{ source_type }` row so the header renders the branded source chip.
    allGroups: (values: unknown[]) =>
      [
        ...new Set(
          (values as Source[][])
            .flat()
            .map((s) => s?.source_type)
            .filter(Boolean)
        ),
      ]
        .sort()
        .map((source_type) => ({ source_type }) as Source),
    matchesGroup: (value: unknown, group: unknown) =>
      ((value as Source[]) ?? []).some((s) => s?.source_type === (group as Source)?.source_type),
  },
  // Every kind of contact in one cell. Hidden: emails beside postal addresses
  // isn't a column anyone reads or edits, so the kinds below are the columns.
  // It stays a field for saved shares and views that still name it.
  {
    key: "contacts",
    type: "contact",
    label: "Contacts",
    icon: "address-book",
    width: 200,
    isArray: true,
    isEditable: false,
    isFilterable: true,
    isHidden: true,
  },
  // One kind of contact each (see CONTACT_AGGREGATES in records.ts). The type
  // fixes the kind a new value is.
  {
    key: "emails",
    type: "email",
    label: "Emails",
    icon: "envelope",
    width: 220,
    isArray: true,
    isEditable: true,
    isFilterable: true,
  },
  {
    key: "phones",
    type: "phone",
    label: "Phones",
    icon: "phone",
    width: 160,
    isArray: true,
    isEditable: true,
    isFilterable: true,
  },
  {
    key: "socials",
    type: "socials",
    label: "Socials",
    icon: "share-alt",
    width: 180,
    isArray: true,
    isEditable: true,
    isFilterable: true,
  },
  {
    key: "locations",
    type: "location",
    label: "Locations",
    icon: "map-marker-alt",
    width: 220,
    isArray: true,
    isEditable: true,
    isFilterable: true,
  },
  {
    key: "times",
    type: "time",
    label: "Times",
    icon: "clock",
    width: 150,
    isArray: true,
    isEditable: true,
    isFilterable: true,
  },
  {
    key: "attachments",
    type: "attachment",
    label: "Attachments",
    icon: "paperclip",
    width: 150,
    isArray: true,
    isEditable: true,
    isFilterable: true,
  },
]

// BUILDER =====================================================================

/** The static aggregate relation fields, enriched through the same `enrichField`
 *  as schema fields — it resolves each aggregate's `value` off the relation.
 *  Lazy and memoized, NOT built eagerly at module load: `enrichField` reads
 *  `records.ts`'s `AGGREGATE_RELATIONS` const, and `records.ts` itself needs
 *  these fields (to recognize an aggregate key in `planRecordWrite`). An eager
 *  top-level build here, imported at records.ts's top level, would make the two
 *  modules cyclic and call `enrichField` while `records.ts` is still mid-module-
 *  eval — `AGGREGATE_RELATIONS` throws "Cannot access ... before initialization"
 *  at that point, since a `const` isn't hoisted the way `enrichField`'s function
 *  declaration is. Deferring the build past both modules' load time avoids it. */
let cached: AugerField[] | null = null
export function getRelationshipFields(): AugerField[] {
  return (cached ??= FIELDS.map((field) => enrichField(field)))
}

// LIST-PARENT FIELDS ==========================================================

/** The `events` / `organizations` / `people` computed relations: the records of
 *  that type joined to this record through a list, either way — the owner of a
 *  list it's on (a guest's event, an employee's company) or the records on a
 *  list it owns (an event's guests, a company's team). `related`-typed, so
 *  `enrichField` reads the relation the key names and the type supplies the
 *  chips, read-only and per-record grouping. */
const LIST_PARENT_FIELDS: ReadonlyArray<EnrichableField> = [
  {
    key: "events",
    type: "related",
    recordType: "event",
    label: "Events",
    icon: "calendar",
    width: 200,
    isArray: true,
  },
  {
    key: "organizations",
    type: "related",
    recordType: "organization",
    label: "Organizations",
    icon: "building",
    width: 200,
    isArray: true,
  },
  {
    key: "people",
    type: "related",
    recordType: "person",
    label: "People",
    icon: "users",
    width: 200,
    isArray: true,
  },
]

// Lazy for the same reason as getRelationshipFields: enrichField lives in
// records.ts, which imports this module.
let listParentsCached: AugerField[] | null = null
export function getListParentFields(): AugerField[] {
  return (listParentsCached ??= LIST_PARENT_FIELDS.map((field) => enrichField(field)))
}

// LIST MEMBERSHIP =============================================================

/** The lists a record is a member of, as a filter field: `lists` hasAny /
 *  hasAll / hasNone a set of list ids, or isEmpty / isNotEmpty. Filter-only
 *  (Postgres reads the record's listings); link-typed so the condition picks
 *  and shows lists like any link to a list. */
export function createListsField(): AugerField {
  return enrichField({
    key: "lists",
    type: "link",
    recordType: "list",
    label: "Lists",
    icon: "list",
    isArray: true,
    isFilterable: true,
    isEditable: false,
    operators: ["hasAny", "hasAll", "hasNone", "isEmpty", "isNotEmpty"],
  })
}

// REVERSE-DIRECTION FIELDS ====================================================
// These match a record by being the TARGET of a link on another record. Keys,
// subtypes and value accessors are the server filter contract.

/**
 * Generate a reverse link field for filtering records that are the TARGET of a
 * link field on another record type.
 *
 * E.g. if an Organisation schema has a "founders" link field pointing to Person
 * records, `createReverseLinkField("founders", "Founders", "organization")`
 * creates a filter field for Person lists matching persons linked as a founder.
 */
export function createReverseLinkField(
  fieldKey: string,
  label: string,
  sourceType: string,
  icon = "link"
): AugerField {
  const read = (item: unknown) =>
    (item as { reverse_links?: Record<string, unknown> } | null)?.reverse_links?.[fieldKey] || []
  return {
    key: `reverse_links.${fieldKey}`,
    type: "backlink",
    subtype: "reverse_links",
    label,
    icon,
    isFilterable: true,
    operators: ["isEmpty", "isNotEmpty", "includes", "hasAny", "hasSchema"],
    linkKey: fieldKey,
    sourceType, // record type that has this link field (e.g. "organization")
    // Resolved relationship rows; no separate stored id, so rawValue === value.
    value: read,
    rawValue: read,
  }
}

/**
 * Build backlink field definitions for a record type. Scans all schemas to find
 * link fields whose recordType matches the target type, creating a displayable
 * field per (schema_id, key) pair.
 *
 * E.g. if an Organisation schema has a "founders" link field pointing to Person,
 * this generates a backlink field for Person views showing which organisations
 * link to each person via that field.
 */
export function buildBacklinkFields(
  targetType: string | null | undefined,
  allSchemas: Schema[]
): AugerField[] {
  if (!targetType || !allSchemas?.length) return []

  const fields: AugerField[] = []

  for (const schema of allSchemas) {
    // Only look at schemas for OTHER record types
    if (!schema.record_type || schema.record_type === targetType) continue
    if (!Array.isArray(schema.fields)) continue

    for (const f of schema.fields) {
      if (f.type !== "link" || f.recordType !== targetType) continue

      const schemaId = schema.id
      const fieldKey = f.key

      // Resolved backlinking records; no separate stored id, so rawValue === value.
      const read = (item: unknown) =>
        (
          (
            item as {
              backlinks?: { schema_id?: number; schema_key?: string; record?: unknown }[]
            } | null
          )?.backlinks || []
        )
          .filter((bl) => bl.schema_id === schemaId && bl.schema_key === fieldKey)
          .map((bl) => withAppearance(bl.record))

      fields.push({
        key: `backlinks.${schemaId}.${fieldKey}`,
        type: "backlink",
        label: `${f.label || fieldKey} (${schema.name})`,
        icon: "share-alt",
        width: 200,
        isEditable: false,
        isFilterable: false,
        schema_id: schemaId,
        linkKey: fieldKey,
        sourceType: schema.record_type,
        value: read,
        rawValue: read,
        // Multi-group: records can appear in multiple backlink groups
        allGroups: (values: unknown[]) => {
          const all = new Map<unknown, { id?: unknown; name?: string }>()
          for (const records of values as { id?: unknown; name?: string }[][]) {
            for (const r of records || []) {
              if (r?.id != null && !all.has(r.id)) all.set(r.id, r)
            }
          }
          return Array.from(all.values()).sort((a, b) => (a.name || "").localeCompare(b.name || ""))
        },
        matchesGroup: (value: unknown, group: unknown) =>
          ((value as { id?: unknown }[]) || []).some(
            (r) => r?.id === (group as { id?: unknown })?.id
          ),
      })
    }
  }

  return fields
}
